import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getRandomSoalMekanik, hitungSkorMekanik, soalMekanik } from './bankSoalMekanik';
import { catatHasilTes } from './progressTracker';
import { useCegahKeluarTes } from './cegahKeluarTes';
import { bacaProgresTes, hapusProgresTes, useAutosaveTes } from './useResumeTes';

function soalDariMetaMekanik(meta) {
  const byId = {};
  soalMekanik.forEach(s => { byId[s.id] = s; });
  return (meta || []).map(m => (byId[m.id] ? { ...byId[m.id] } : null)).filter(Boolean);
}

// ─── Konstanta ───────────────────────────────────────────────
const TOTAL_SOAL  = 12;
const WAKTU_MENIT = 15;
const WAKTU_TOTAL = WAKTU_MENIT * 60;

// ─── Helper ──────────────────────────────────────────────────
function formatWaktu(detik) {
  const m = Math.floor(detik / 60).toString().padStart(2, '0');
  const s = (detik % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function getBadge(skor) {
  if (skor >= 90) return { label: 'Luar Biasa', color: 'text-green-700 bg-green-100 border-green-300' };
  if (skor >= 75) return { label: 'Baik', color: 'text-blue-700 bg-blue-100 border-blue-300' };
  if (skor >= 60) return { label: 'Cukup', color: 'text-yellow-700 bg-yellow-100 border-yellow-300' };
  return { label: 'Perlu Latihan', color: 'text-red-700 bg-red-100 border-red-300' };
}

function BannerLanjutkanMekanik({ progresTersimpan, onLanjutkan, onMulaiBaru }) {
  if (!progresTersimpan) return null;
  const totalSoal = progresTersimpan.soalMeta?.length || 0;
  return (
    <div className="rounded-xl p-4 mb-5 border-2 border-orange-400 bg-orange-50">
      <p className="text-xs font-bold uppercase tracking-wide text-orange-700 mb-1">⏸️ Ada Pengerjaan Sebelumnya</p>
      <p className="text-sm text-orange-800 mb-3">
        Baru sampai soal <b>{(progresTersimpan.indeks || 0) + 1}/{totalSoal}</b>, sisa waktu <b>{formatWaktu(progresTersimpan.waktuSisa || 0)}</b>.
      </p>
      <div className="flex gap-2">
        <button onClick={onLanjutkan} className="flex-1 py-2 rounded-lg bg-orange-600 text-white text-sm font-bold hover:bg-orange-700 transition">
          Lanjutkan
        </button>
        <button onClick={onMulaiBaru} className="flex-1 py-2 rounded-lg border border-orange-300 text-orange-600 text-sm font-semibold hover:bg-orange-100 transition">
          Mulai Baru
        </button>
      </div>
    </div>
  );
}

// ─── Layar Intro ───────────────────────────────────────────────
function LayarIntro({ onMulai, onBack, tryoutMode, progresTersimpan, onLanjutkan, onMulaiBaru }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">⚙️</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Tes Bakat Mekanik</h1>
          <p className="text-gray-400 text-sm mt-1">
            {tryoutMode ? 'Bagian dari Tryout Resmi' : 'Roda gigi, katrol, tuas, dan prinsip mekanika dasar'}
          </p>
        </div>

        <BannerLanjutkanMekanik progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

        <div className="rounded-xl p-4 mb-5 border-2 border-orange-200 bg-orange-50">
          <p className="text-xs font-bold uppercase tracking-wide text-orange-700 mb-1">📌 {tryoutMode ? 'Bagian Tryout' : 'Info Modul'}</p>
          <p className="text-sm text-orange-800 leading-relaxed">
            Modul ini mengukur pemahaman prinsip mekanika sederhana (roda gigi, katrol, tuas, bidang miring, dsb) — {tryoutMode ? 'ikut dihitung ke skor Tryout resmi.' : 'sudah ikut dihitung ke skor Tryout resmi.'}
          </p>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-5 grid grid-cols-3 gap-3 text-center">
          <div><p className="text-lg font-bold text-gray-800">{TOTAL_SOAL}</p><p className="text-xs text-gray-500">Soal</p></div>
          <div><p className="text-lg font-bold text-gray-800">{WAKTU_MENIT}'</p><p className="text-xs text-gray-500">Waktu</p></div>
          <div><p className="text-lg font-bold text-gray-800">~75"</p><p className="text-xs text-gray-500">Per soal</p></div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onBack}
            className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition"
          >
            ← Kembali
          </button>
          <button
            onClick={onMulai}
            className="flex-grow py-3 rounded-xl font-bold border-2 border-orange-400 bg-orange-50 text-orange-700 hover:opacity-90 transition"
          >
            Mulai Sekarang →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Soal Mekanik ───────────────────────────────────────────────
function SoalMekanik({ soal, onJawab, jawaban }) {
  const huruf = ['a', 'b', 'c', 'd'];
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="bg-orange-100 text-orange-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
          ⚙️ Mekanik
        </span>
        <span className="text-xs text-gray-400">Pilih jawaban yang tepat</span>
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-4 flex items-center justify-center">
        <div className="w-full max-w-xs" style={{ aspectRatio: '200 / 140' }} dangerouslySetInnerHTML={{ __html: soal.gambar }} />
      </div>
      <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 mb-4">
        <p className="text-base font-semibold text-gray-800 leading-relaxed">{soal.soal}</p>
      </div>
      <div className="space-y-2">
        {huruf.map(h => (
          (h in soal.pilihan) && (
            <button
              key={h}
              onClick={() => onJawab(h)}
              className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium transition text-sm
                ${jawaban === h
                  ? 'border-orange-500 bg-orange-100 text-orange-800'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-orange-300 hover:bg-orange-50'}`}
            >
              <span className="font-bold mr-2 uppercase">{h}.</span>
              {soal.pilihan[h]}
            </button>
          )
        ))}
      </div>
    </div>
  );
}

// ─── Layar Hasil ──────────────────────────────────────────────
function LayarHasil({ soalList, jawabanUser, onUlang, onBack, tryoutMode, tryoutId }) {
  const [showPembahasan, setShowPembahasan] = useState(false);
  const hasil = hitungSkorMekanik(soalList, jawabanUser);
  const badge = getBadge(hasil.skor);

  useEffect(() => {
    catatHasilTes('mekanik', hasil.skor, { benar: hasil.benar, salah: hasil.salah, kosong: hasil.kosong, total: hasil.total }, tryoutMode, tryoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          <div className="text-4xl mb-2">
            {hasil.skor >= 75 ? '🏆' : hasil.skor >= 60 ? '👍' : '📚'}
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-3">Hasil Tes Bakat Mekanik</h2>
          <div className="text-6xl font-extrabold text-orange-600 mb-1">{hasil.skor}</div>
          <p className="text-gray-400 text-sm mb-3">Skor Total</p>
          <span className={`inline-block px-4 py-1 rounded-full text-sm font-bold border ${badge.color}`}>
            {badge.label}
          </span>
          <div className="w-full bg-gray-200 rounded-full h-3 mt-4 mb-2">
            <div className="bg-orange-500 h-3 rounded-full transition-all duration-1000" style={{ width: `${hasil.skor}%` }} />
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs mt-4">
            <div className="bg-green-50 rounded-lg py-2">
              <p className="font-bold text-green-700 text-base">{hasil.benar}</p>
              <p className="text-gray-500">Benar</p>
            </div>
            <div className="bg-red-50 rounded-lg py-2">
              <p className="font-bold text-red-700 text-base">{hasil.salah}</p>
              <p className="text-gray-500">Salah</p>
            </div>
            <div className="bg-gray-50 rounded-lg py-2">
              <p className="font-bold text-gray-600 text-base">{hasil.kosong}</p>
              <p className="text-gray-500">Kosong</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow p-4 space-y-3">
          <button
            onClick={() => setShowPembahasan(!showPembahasan)}
            className="w-full py-3 rounded-xl border border-orange-300 text-orange-600 font-semibold hover:bg-orange-50 transition"
          >
            {showPembahasan ? 'Sembunyikan' : '📋 Lihat'} Pembahasan
          </button>
          <div className="flex gap-3">
            <button onClick={onUlang} className="flex-1 py-3 rounded-xl bg-orange-600 text-white font-bold hover:bg-orange-700 transition">
              🔄 Ulangi
            </button>
            <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">
              ← Menu
            </button>
          </div>
        </div>

        {showPembahasan && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Pembahasan Soal</p>
            {soalList.map((soal, idx) => {
              const userJawab = jawabanUser.find(j => j.id === soal.id);
              const jwb = userJawab?.jawaban;
              const benar = jwb === soal.jawaban;
              return (
                <div key={soal.id} className={`bg-white rounded-xl p-4 border-l-4 ${benar ? 'border-green-500' : 'border-red-400'}`}>
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                      {idx + 1}. ⚙️ Mekanik
                    </span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${benar ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {benar ? '✓ Benar' : '✗ Salah'}
                    </span>
                  </div>
                  <div className="w-full max-w-[220px] mb-2" style={{ aspectRatio: '200 / 140' }} dangerouslySetInnerHTML={{ __html: soal.gambar }} />
                  <p className="text-sm text-gray-700 mb-2">{soal.soal}</p>
                  <p className="text-sm">
                    <span className="text-gray-400">Jawabanmu: </span>
                    <span className={`font-bold ${benar ? 'text-green-600' : 'text-red-500'}`}>
                      {jwb ? `${jwb.toUpperCase()}. ${soal.pilihan[jwb]}` : '—'}
                    </span>
                    {!benar && (
                      <span className="block mt-1 text-gray-400">
                        Benar: <span className="font-bold text-green-600">{soal.jawaban.toUpperCase()}. {soal.pilihan[soal.jawaban]}</span>
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 bg-gray-50 rounded-lg px-3 py-2">💡 {soal.pembahasan}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Komponen Utama ───────────────────────────────────────────
export default function MekanikModul({ onBack, tryoutMode = false, tryoutId = null }) {
  const [fase, setFase]           = useState('intro');
  useCegahKeluarTes(fase === 'tes', onBack);
  const [soalList, setSoalList]   = useState([]);
  const [indeks, setIndeks]       = useState(0);
  const [jawabanUser, setJawabanUser] = useState([]);
  const [waktuSisa, setWaktuSisa] = useState(WAKTU_TOTAL);
  const timerRef                  = useRef(null);
  const [progresTersimpan, setProgresTersimpan] = useState(null);

  useEffect(() => {
    setProgresTersimpan(bacaProgresTes('mekanik', tryoutId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAutosaveTes(fase === 'tes', 'mekanik', tryoutId, {
    soalMeta: soalList.map(s => ({ id: s.id })),
    indeks,
    jawabanUser,
    waktuSisa,
  });

  const handleMulai = useCallback(() => {
    hapusProgresTes('mekanik', tryoutId);
    setProgresTersimpan(null);
    const soal = getRandomSoalMekanik(TOTAL_SOAL);
    setSoalList(soal);
    setIndeks(0);
    setJawabanUser(soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(WAKTU_TOTAL);
    setFase('tes');
  }, [tryoutId]);

  const handleLanjutkan = useCallback(() => {
    if (!progresTersimpan) return;
    const soal = soalDariMetaMekanik(progresTersimpan.soalMeta);
    setSoalList(soal);
    setIndeks(progresTersimpan.indeks || 0);
    setJawabanUser(progresTersimpan.jawabanUser || soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(progresTersimpan.waktuSisa || WAKTU_TOTAL);
    setFase('tes');
  }, [progresTersimpan]);

  const handleMulaiBaruDariBanner = useCallback(() => {
    hapusProgresTes('mekanik', tryoutId);
    setProgresTersimpan(null);
  }, [tryoutId]);

  useEffect(() => {
    if (fase !== 'tes') return;
    timerRef.current = setInterval(() => {
      setWaktuSisa(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          hapusProgresTes('mekanik', tryoutId);
          setFase('hasil');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [fase, tryoutId]);

  const handleJawab = useCallback((nilai) => {
    setJawabanUser(prev =>
      prev.map(j => j.id === soalList[indeks].id ? { ...j, jawaban: nilai } : j)
    );
  }, [indeks, soalList]);

  const handleNext = useCallback(() => {
    if (indeks < soalList.length - 1) setIndeks(i => i + 1);
    else { clearInterval(timerRef.current); hapusProgresTes('mekanik', tryoutId); setFase('hasil'); }
  }, [indeks, soalList.length, tryoutId]);

  const handlePrev = useCallback(() => {
    if (indeks > 0) setIndeks(i => i - 1);
  }, [indeks]);

  const handleSelesai = useCallback(() => {
    clearInterval(timerRef.current);
    hapusProgresTes('mekanik', tryoutId);
    setFase('hasil');
  }, [tryoutId]);

  const handleUlang = useCallback(() => {
    setFase('intro');
    setSoalList([]);
    setIndeks(0);
    setJawabanUser([]);
    setWaktuSisa(WAKTU_TOTAL);
  }, []);

  if (fase === 'intro') return (
    <LayarIntro
      onMulai={handleMulai}
      onBack={onBack}
      tryoutMode={tryoutMode}
      progresTersimpan={progresTersimpan}
      onLanjutkan={handleLanjutkan}
      onMulaiBaru={handleMulaiBaruDariBanner}
    />
  );

  if (fase === 'hasil') {
    return (
      <LayarHasil
        tryoutMode={tryoutMode}
        tryoutId={tryoutId}
        soalList={soalList}
        jawabanUser={jawabanUser}
        onUlang={handleUlang}
        onBack={onBack}
      />
    );
  }

  const soal = soalList[indeks];
  if (!soal) return null;

  const jawabanSoalIni = jawabanUser.find(j => j.id === soal.id)?.jawaban;
  const progress       = ((indeks + 1) / soalList.length) * 100;
  const warnaMeter     = waktuSisa < 60 ? 'bg-red-500' : waktuSisa < 180 ? 'bg-yellow-400' : 'bg-green-500';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className={`font-mono font-bold text-lg px-3 py-1 rounded-lg
              ${waktuSisa < 60 ? 'text-red-600 bg-red-50' : 'text-gray-700 bg-gray-100'}`}>
              ⏱ {formatWaktu(waktuSisa)}
            </div>
            <span className="text-sm text-gray-500 font-semibold">
              Soal <span className="text-orange-600">{indeks + 1}</span> / {soalList.length}
            </span>
            <span className="text-xs font-bold px-2 py-1 rounded-full bg-orange-100 text-orange-700">
              ⚙️ Mekanik
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className={`h-2 rounded-full transition-all duration-300 ${warnaMeter}`} style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-white rounded-2xl shadow p-6 mb-4">
          <SoalMekanik soal={soal} onJawab={handleJawab} jawaban={jawabanSoalIni} />
        </div>

        <div className="flex gap-3 mb-4">
          <button
            onClick={handlePrev}
            disabled={indeks === 0}
            className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Sebelumnya
          </button>
          {indeks < soalList.length - 1 ? (
            <button onClick={handleNext} className="flex-grow py-3 rounded-xl bg-orange-600 text-white font-bold hover:bg-orange-700 transition">
              Selanjutnya →
            </button>
          ) : (
            <button onClick={handleSelesai} className="flex-grow py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 transition">
              Selesai ✓
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-1 justify-center">
          {soalList.map((s, i) => {
            const dijawab    = jawabanUser.find(j => j.id === s.id)?.jawaban;
            const sudahDiisi = dijawab !== null && dijawab !== undefined && dijawab !== '';
            return (
              <button
                key={s.id}
                onClick={() => setIndeks(i)}
                className={`w-8 h-8 text-xs rounded-lg font-semibold transition
                  ${i === indeks       ? 'bg-orange-600 text-white'
                  : sudahDiisi        ? 'bg-green-500 text-white'
                  : 'bg-orange-100 text-orange-600 hover:bg-orange-200'}`}
              >
                {i + 1}
              </button>
            );
          })}
        </div>
        <p className="text-center text-xs text-gray-400 mt-2">
          🟧 Aktif &nbsp;|&nbsp; 🟩 Sudah dijawab &nbsp;|&nbsp; ⬜ Belum dijawab
        </p>
      </div>
    </div>
  );
}
